/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentInfoCardKernel,
    InfoCardKernel,
    InfoCardKernelType,
    UnifiedCollectionInfoCardKernel,
    VodOfferInfoCardKernel,
} from 'client-code/actions';
import {
    ContentVodItemKernel,
    OfferVodItemKernel,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemType,
} from 'client-code/vod-browse';

import {
    IInfoCard,
    IInfoCardProperty,
    InfoCardManager,
    InfoCardUserInputHandler,
} from '../infocard';

export class VodBrowseInfoCardUserInputHandler extends InfoCardUserInputHandler {
    private mInfoCard: IInfoCard = InfoCardManager.getInstance();

    private horizontalKeyNavigationHelper(direction: 'left' | 'right'): void {
        const { activeItemIndex, itemList } = (this.mInfoCard.getContextData(
            VodBrowseInfoCardUserInputHandler.name,
        ) || {}) as { activeItemIndex: number; itemList: VodItem[] };

        // activeItemIndex is always a non-folder item and navigableItems is always > 1.
        const navigableItems = itemList.filter(
            (item) => item.vodItemType.toLowerCase() !== VodItemType.FolderVodItem.toLowerCase(),
        );
        const currentNavigableIndex = navigableItems.indexOf(itemList[activeItemIndex]);
        const nextNavigableIndex =
            direction === 'left'
                ? (currentNavigableIndex - 1 + navigableItems.length) % navigableItems.length
                : (currentNavigableIndex + 1) % navigableItems.length;
        const nextItem = navigableItems[nextNavigableIndex];
        const nextItemIndex = itemList.indexOf(nextItem);

        this.mInfoCard.setInfoCardKernel(this.getInfoCardKernel(nextItem));
        this.mInfoCard.setContextData(VodBrowseInfoCardUserInputHandler.name, {
            activeItemIndex: nextItemIndex,
            itemList,
        });
    }

    /**
     * @override
     */
    public onKeyLeft(properties: IInfoCardProperty): void {
        if (!properties.hasItemsBefore) return;
        this.horizontalKeyNavigationHelper('left');
    }

    /**
     * @override
     */
    public onKeyRight(properties: IInfoCardProperty): void {
        if (!properties.hasItemsAfter) return;
        this.horizontalKeyNavigationHelper('right');
    }

    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernel(item: VodItem): InfoCardKernel {
        switch (item.vodItemType.toLowerCase()) {
            case VodItemType.EpisodeVodItem.toLowerCase():
                return {
                    infoCardKernelType: InfoCardKernelType.ContentInfoCardKernel,
                    contentId: (item.vodItemKernel as ContentVodItemKernel).contentId,
                } as ContentInfoCardKernel;

            case VodItemType.MovieVodItem.toLowerCase():
            case VodItemType.SpecialVodItem.toLowerCase():
                return {
                    infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
                    offerId: (item.vodItemKernel as OfferVodItemKernel).offerId,
                } as VodOfferInfoCardKernel;

            default:
                return {
                    infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                    unifiedCollectionId: (item.vodItemKernel as UnifiedCollectionVodItemKernel)
                        .unifiedCollectionId,
                } as UnifiedCollectionInfoCardKernel;
        }
    }
}
